import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MultiPortfolioContext = createContext(null);

export const useMultiPortfolio = () => {
  const context = useContext(MultiPortfolioContext);
  if (!context) {
    throw new Error("useMultiPortfolio must be used within MultiPortfolioProvider");
  }
  return context;
};

// Navigation callback - will be set by App.js inside BrowserRouter
let navigationCallback = null;
export const setNavigationCallback = (callback) => {
  navigationCallback = callback;
};

export const MultiPortfolioProvider = ({ children }) => {
  const { user } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [currentPortfolio, setCurrentPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [switchKey, setSwitchKey] = useState(0); // Key to force re-render of children

  const fetchPortfolios = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const [portfoliosRes, currentRes] = await Promise.all([
        axios.get(`${API_URL}/api/portfolios`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portfolios/current`, { withCredentials: true }),
      ]);
      setPortfolios(portfoliosRes.data || []);
      setCurrentPortfolio(currentRes.data || null);
    } catch (error) {
      console.error("Fetch portfolios error:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const createPortfolio = async (name, description) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/portfolios`,
        { name, description },
        { withCredentials: true }
      );
      const newPortfolio = response.data.portfolio;
      await fetchPortfolios();
      return newPortfolio;
    } catch (error) {
      throw error.response?.data?.detail || "Failed to create portfolio";
    }
  };

  const updatePortfolio = async (portfolioId, data) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/portfolios/${portfolioId}`,
        data,
        { withCredentials: true }
      );
      await fetchPortfolios();
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || "Failed to update portfolio";
    }
  };

  const deletePortfolio = async (portfolioId) => {
    try {
      await axios.delete(`${API_URL}/api/portfolios/${portfolioId}`, { withCredentials: true });
      await fetchPortfolios();
    } catch (error) {
      throw error.response?.data?.detail || "Failed to delete portfolio";
    }
  };

  const switchPortfolio = async (portfolioId) => {
    setSwitching(true);
    try {
      await axios.post(
        `${API_URL}/api/portfolios/${portfolioId}/set-default`,
        {},
        { withCredentials: true }
      );
      // Refresh portfolio data
      await fetchPortfolios();
      // Increment switchKey to force all children to re-fetch their data
      setSwitchKey(prev => prev + 1);
      // Small delay to show the switching state for UX
      await new Promise(resolve => setTimeout(resolve, 500));
      // NO navigation - just update the data and let PortfolioProvider refresh
      // The switchKey change will trigger ProtectedRoute to remount PortfolioProvider
    } catch (error) {
      throw error.response?.data?.detail || "Failed to switch portfolio";
    } finally {
      setSwitching(false);
    }
  };

  const canCreateMore = portfolios.length < 3;

  // Render full-page loader when switching - this blocks all children
  if (switching) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 mx-auto border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Switching portfolio...</p>
        </div>
      </div>
    );
  }

  return (
    <MultiPortfolioContext.Provider
      value={{
        portfolios,
        currentPortfolio,
        loading,
        switching,
        switchKey,
        createPortfolio,
        updatePortfolio,
        deletePortfolio,
        switchPortfolio,
        canCreateMore,
        refreshPortfolios: fetchPortfolios,
      }}
    >
      {children}
    </MultiPortfolioContext.Provider>
  );
};

export default MultiPortfolioProvider;
